import { useEffect, useState } from "react";

function base64UrlToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from(raw, (character) => character.charCodeAt(0));
}

async function request(operacion, datos = {}) {
  const response = await fetch("/api/dashboard/push/operacion", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ operacion, datos }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || !payload.ok)
    throw new Error(payload.mensaje || "No fue posible actualizar las notificaciones.");
  return payload.data || {};
}

export function AdminPushNotifications({ deliveryOnly = false }) {
  const [state, setState] = useState(null);
  const [status, setStatus] = useState("Cargando notificaciones…");
  const [busy, setBusy] = useState(false);

  async function load() {
    const data = await request("estado");
    setState(data);
    return data;
  }

  useEffect(() => {
    let active = true;
    load().then(() => active && setStatus("")).catch((error) => active && setStatus(error.message));
    return () => { active = false; };
  }, []);

  async function activate() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setStatus("Este navegador no permite notificaciones Push. Prueba con el panel instalado como aplicación.");
      return;
    }
    if (!state?.configurada) {
      setStatus("Las notificaciones aún no están configuradas en el servidor.");
      return;
    }
    if (!state?.puede_recibir_pedidos) {
      setStatus("Tu usuario no tiene permiso para recibir avisos de pedidos.");
      return;
    }
    setBusy(true);
    setStatus("Solicitando permiso al navegador…");
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("No se concedió permiso para mostrar notificaciones.");
      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64UrlToUint8Array(state.clave_publica_vapid),
        });
      }
      await request("suscribir", { subscription: subscription.toJSON() });
      await load();
      setStatus("Notificaciones activadas en este dispositivo.");
    } catch (error) {
      setStatus(error.message || "No fue posible activar las notificaciones.");
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(subscriptionId) {
    setBusy(true);
    setStatus("Desactivando notificaciones…");
    try {
      const registration = await navigator.serviceWorker?.ready;
      const browserSubscription = await registration?.pushManager.getSubscription();
      await request("revocar", { subscription_id: subscriptionId });
      await browserSubscription?.unsubscribe();
      await load();
      setStatus("Notificaciones desactivadas en este dispositivo.");
    } catch (error) {
      setStatus(error.message || "No fue posible desactivar las notificaciones.");
    } finally {
      setBusy(false);
    }
  }

  const subscriptions = state?.suscripciones || [];
  return (
    <section className="module-panel admin-push-notifications" aria-label="Notificaciones de pedidos">
      <p className="eyebrow">NOTIFICACIONES</p>
      <h2>{deliveryOnly ? "Nuevas paradas en este dispositivo" : "Pedidos nuevos en este dispositivo"}</h2>
      <p>{deliveryOnly ? "Activa avisos para recibir pedidos que se agreguen a tu ruta, aunque la aplicación esté cerrada." : "Activa avisos para recibir nuevos pedidos aunque el panel esté cerrado. Solo se guarda la suscripción técnica de este navegador."}</p>
      {!state?.puede_recibir_pedidos && state && <p className="push-status warning">Tu usuario no tiene permiso para recibir estos avisos.</p>}
      {!state?.configurada && state && <p className="push-status warning">El canal Push está pendiente de configuración segura en el servidor.</p>}
      {subscriptions.length ? (
        <div className="push-device-list">
          {subscriptions.map((subscription) => (
            <div key={subscription.subscription_id}>
              <span>Notificaciones activas en este dispositivo</span>
              <button type="button" className="secondary-action" onClick={() => deactivate(subscription.subscription_id)} disabled={busy}>Desactivar</button>
            </div>
          ))}
        </div>
      ) : null}
      <button type="button" className="primary-action" onClick={activate} disabled={busy || !state?.configurada || !state?.puede_recibir_pedidos}>
        {busy ? "Actualizando…" : subscriptions.length ? "Activar en otro dispositivo" : "Activar notificaciones"}
      </button>
      {status && <p className="push-status" role="status">{status}</p>}
    </section>
  );
}
